import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '../../services/storage';

import {
  APIBusinessTeam,
  APIConsole,
  APIHierarchy,
  APIPreferences,
  APISite,
  AssetName,
  AssetType,
  BusinessTeam,
  Console,
  DateTime,
  Hierarchy,
  Post,
  Preferences,
  Site,
  UserPreferences,
} from '../../services/dataModels';
import { Event, LogErrorType, trackEvent, trackSerializedError } from '../../services/Analytics';
import { actions as userActions } from './user';
import { getErrorDetails, NetworkErrorDetails } from '../../services/networkUtils';
import { ThunkExtra, RootState } from '../store';

// TODO: move this to a utils in `/services` or something
// import { createHierarchy, getPreferencesFromHierarchy } from './services';

const createHierarchy = (apiHierarchy: APIHierarchy): Hierarchy => {
  const hierarchy: Hierarchy = {
    sites: Object.assign({}, ...apiHierarchy.sites.map((s) => ({ [s.assetName]: createSite(s) }))),
  };
  return hierarchy;
};

const getPreferencesFromHierarchy = (
  apiPreferences: APIPreferences,
  hierarchy: Hierarchy,
): Preferences => {
  const preferredSite = hierarchy.sites[apiPreferences.site];
  const preferredBusinessTeam = preferredSite?.businessTeams[apiPreferences.businessTeam];
  const preferredConsole = preferredBusinessTeam?.consoles[apiPreferences.console];
  const preferredPost = preferredConsole?.posts[apiPreferences.post];

  const preferences: Preferences = {
    site: preferredSite,
    businessTeam: preferredBusinessTeam,
    console: preferredConsole,
    post: preferredPost,
    selectedAsset: preferredPost
      ? preferredPost
      : preferredConsole
      ? preferredConsole
      : preferredBusinessTeam
      ? preferredBusinessTeam
      : preferredSite,
  };

  return preferences;
};

const createSite = (apiSite: APISite): Site => {
  const site: Site = {
    ...apiSite,
    businessTeams: Object.assign(
      {},
      ...apiSite.businessTeams.map((b) => ({ [b.assetName]: createBusinessTeam(b) })),
    ),
  };
  return site;
};

const createBusinessTeam = (apiBusinessTeam: APIBusinessTeam): BusinessTeam => {
  const businessTeam: BusinessTeam = {
    ...apiBusinessTeam,
    consoles: Object.assign(
      {},
      ...apiBusinessTeam.consoles.map((c) => ({ [c.assetName]: createConsole(c) })),
    ),
  };
  return businessTeam;
};

const createConsole = (apiConsole: APIConsole): Console => {
  const console: Console = {
    ...apiConsole,
    posts: Object.assign({}, ...apiConsole.posts.map((p) => ({ [p.assetName]: p }))),
  };
  return console;
};

const loadHierarchy = createAsyncThunk<
  APIHierarchy,
  undefined,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>('preferences/loadHierarchy', async (_, { rejectWithValue, extra: { apiClient } }) => {
  try {
    const hierarchyResponse = await apiClient.getAssetHierarchy();
    return hierarchyResponse.data;
  } catch (error) {
    return rejectWithValue(getErrorDetails(error));
  }
}, {
  // Skip if a fetch is already in-flight to avoid concurrent duplicate requests.
  condition: (_, { getState }) => !(getState() as RootState).preference.isLoading,
});

const getPreferencesForSite = createAsyncThunk<
  UserPreferences,
  string,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'preferences/getPreferencesForSite',
  async (siteCode: string, { rejectWithValue, extra: { apiClient } }) => {
    try {
      const preferencesResponse = await apiClient.getUserPreferences(siteCode);
      return preferencesResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
  {
    // Skip if a fetch is already in-flight to avoid concurrent duplicate requests.
    condition: (_, { getState }) => !(getState() as RootState).preference.isLoading,
  },
);

interface SavePreferencesArgs {
  preferences: Preferences;
  selectedAsset: AssetName;
}

const savePreferences = createAsyncThunk<
  UserPreferences,
  SavePreferencesArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'preferences/savePreferences',
  async (args: SavePreferencesArgs, { rejectWithValue, extra: { apiClient } }) => {
    try {
      const preferencesResponse = await apiClient.setUserPreferences(
        args.preferences.site?.siteCode,
        args.selectedAsset,
      );
      // Eventually will be removed because redux persist saves things in AsyncStorage
      // Used for the other screens currently.
      await AsyncStorage.setItem('preferences', JSON.stringify(args.preferences));
      return preferencesResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

export type PreferenceState = {
  preferences: Preferences;
  hierarchy?: Hierarchy;
  shiftName: string;
  currentShiftStart: DateTime;
  currentShiftEnd: DateTime;
  previousShiftStart: DateTime;
  previousShiftEnd: DateTime;
  isLoading: boolean;
  error?: string;
  modal?: AssetType;
};

const initialState: PreferenceState = {
  preferences: {},
  shiftName: '',
  currentShiftStart: '',
  currentShiftEnd: '',
  previousShiftStart: '',
  previousShiftEnd: '',
  isLoading: false,
};

const preferenceSlice = createSlice({
  name: 'preference',
  initialState: initialState,
  reducers: {
    setPreferredSite(state, { payload }: PayloadAction<Site | undefined>) {
      state.preferences.selectedAsset = payload;
      state.preferences.site = payload;
      state.preferences.businessTeam = undefined;
      state.preferences.console = undefined;
      state.preferences.post = undefined;
    },
    setPreferredBusinessTeam(state, { payload }: PayloadAction<BusinessTeam | undefined>) {
      state.preferences.selectedAsset = payload === undefined ? state.preferences.site : payload;
      state.preferences.businessTeam = payload;
      state.preferences.console = undefined;
      state.preferences.post = undefined;
    },
    setPreferredConsole(state, { payload }: PayloadAction<Console | undefined>) {
      state.preferences.selectedAsset =
        payload === undefined ? state.preferences.businessTeam : payload;
      state.preferences.console = payload;
      state.preferences.post = undefined;
    },
    setPreferredPost(state, { payload }: PayloadAction<Post | undefined>) {
      state.preferences.selectedAsset = payload === undefined ? state.preferences.console : payload;
      state.preferences.post = payload;
    },
    assetTypeSelected(state, { payload }: PayloadAction<AssetType | undefined>) {
      if (typeof payload !== 'undefined') {
        trackEvent(Event.OpenedTemplateSelectionModal);
      }
      state.modal = payload;
    },
    resetErrorMessage(state) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userActions.logout.fulfilled, (_state) => {
      return initialState;
    });
    builder
      .addCase(loadHierarchy.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(loadHierarchy.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = undefined;
        state.hierarchy = createHierarchy(payload);
        if (!payload.sites.some((s) => s.siteCode === state.preferences?.site?.siteCode)) {
          state.preferences.site = undefined;
          state.preferences.businessTeam = undefined;
          state.preferences.console = undefined;
          state.preferences.post = undefined;
        }
      })
      .addCase(loadHierarchy.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        // Keep the previously loaded tree: the Preferences screen shows it while a refresh runs.

        switch (networkError.type) {
          case 'unauthorized':
            state.error = 'Something went wrong. Unable to refresh site and assets.';
            break;
          default:
            state.error = 'Something went wrong. Please try again.';
            break;
        }
        trackSerializedError(networkError, LogErrorType.RefreshHierarchy);
      });

    builder
      .addCase(getPreferencesForSite.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getPreferencesForSite.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = undefined;
        state.preferences = getPreferencesFromHierarchy(payload.preferences, state.hierarchy);
        state.preferences.path = payload.path;
        state.shiftName = payload.shiftName;
        state.currentShiftStart = payload.currentShiftStart;
        state.currentShiftEnd = payload.currentShiftEnd;
        state.previousShiftStart = payload.previousShiftStart;
        state.previousShiftEnd = payload.previousShiftEnd;
      })
      .addCase(getPreferencesForSite.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        trackSerializedError(networkError, LogErrorType.RefreshPreferences);
      });

    builder
      .addCase(savePreferences.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(savePreferences.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = undefined;
        state.preferences.path = payload.path;
        // may need to save the additional information here from the payload for other things.
        // but preferences is not returned when being successfully saved.
        // state.preferences = payload.preferences;
      })
      .addCase(savePreferences.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;

        switch (networkError.type) {
          case 'unauthorized':
          case 'forbidden':
            state.error = `User may not have sufficient permissions to access the selected asset: ${state.preferences.selectedAsset.displayName}. \nPlease try selecting another asset.`;
            break;
          default:
            state.error = 'Something went wrong. Please try again.';
            break;
        }
        trackSerializedError(networkError, LogErrorType.SavePreferences);
      });
  },
});

export default preferenceSlice.reducer;

export const actions = {
  ...preferenceSlice.actions,
  loadHierarchy,
  getPreferencesForSite,
  savePreferences,
};
